import { EventToolbox } from 'client/utils/event-toolbox';
import { PAGE_EVENTS } from 'client/constants/page-events';
import { EVENTS } from 'client/utils/event-constants';
import { UPDATE_SESSION } from 'client/actions/constants';
import { PUB_STATES } from 'client/constants/pub-states';
import { featureFlags as featureFlagsUtil } from 'client/utils/feature-flags';
import { injectScript } from 'client/utils/inject-script';
import { MCP_AGENT_CODRIVER } from 'site-modules/shared/constants/mcp/agents';
import { getMcpSynpartner } from './mcp-synpartner';
import { isServerSidePayloadEventsEnabled, processServerSidePayloadEvent } from './gtm-tags/server-side-payload-events';

let trackingEventQueue = null;
let isIframe;
let venomEnv;

export function inNonEdmundsIframe() {
  try {
    return window.self !== window.top && !window.top.location.origin.includes('edmunds.com');
  } catch {
    return true;
  }
}

function pushGtmDataLayerItem(dataLayerItem) {
  if (isServerSidePayloadEventsEnabled()) {
    processServerSidePayloadEvent(dataLayerItem, venomEnv);
  } else {
    window.dataLayer.push(dataLayerItem);
  }
}

function processItem(trackingEvent) {
  if (window.requestIdleCallback) {
    // new browsers will be more performant
    window.requestIdleCallback(() => pushGtmDataLayerItem(trackingEvent));
  } else {
    pushGtmDataLayerItem(trackingEvent);
  }
}

function isQueueEnabled() {
  return trackingEventQueue !== null;
}

function startQueueing() {
  trackingEventQueue = [];
}

function stopQueueing() {
  trackingEventQueue = null;
}

function putInQueue(trackingEvent) {
  trackingEventQueue.push(trackingEvent);
}

function processQueue(pageEnterEvent) {
  let queue = [];
  if (pageEnterEvent) {
    queue = [pageEnterEvent, ...trackingEventQueue];
  } else {
    queue = [...trackingEventQueue];
  }

  queue.forEach(trackingEvent => {
    processItem(trackingEvent);
  });
}

function simplifyColorName(sourceString) {
  return sourceString ? sourceString.replace(/^"|\|.*|"$/g, '') : sourceString;
}

export function handleTrackAction(event, disableEDWPastLoad, codriverMessageUUID) {
  const eventobj = event.detail;

  // no EDW pixels are fired on the page past the on load events when disableEDWpastload url param is true
  const disableEDWpastload = disableEDWPastLoad && eventobj?.event_data?.action_cause !== 'page_load';

  const disableEDWEmbed = isIframe && eventobj?.event_data?.action_name !== 'widget_view';

  if (!eventobj || disableEDWpastload || disableEDWEmbed) return;

  let trackingEvent;
  if (eventobj.event_type) {
    // new tracking event
    trackingEvent = {
      ts: eventobj.ts || Date.now(),
      event: eventobj.event_type, // define actual trigger event for GTM
      ...eventobj,
    };
  } else {
    // tracking context update only
    trackingEvent = {
      ...eventobj,
    };
  }

  if (codriverMessageUUID) {
    trackingEvent = {
      ...trackingEvent,
      event_data: {
        ...trackingEvent.event_data,
        resp_uuid: codriverMessageUUID,
      },
    };
  }

  if (isQueueEnabled()) {
    // put tracking events into the queue until 'page_enter' event
    if (eventobj.event_type && eventobj.event_type === 'page_enter') {
      processQueue(trackingEvent);
      stopQueueing();
    } else {
      putInQueue(trackingEvent);
    }
  } else {
    processItem(trackingEvent);
  }
}

/**
 * Returns boolean after checking feature flag 'disableEDW'.
 *
 * @param  {Object} state
 */
export function usesDisableEDW({ featureFlags }) {
  return !!(featureFlags && featureFlags.disableEDW);
}

/**
 * Returns boolean after checking feature flag 'disableEDWpastload'.
 *
 * @param  {Object} state
 */
export function usesDisableEDWpastload({ featureFlags }) {
  return !!(featureFlags && featureFlags.disableEDWpastload);
}

function getCodriverMessageUUID(isMcpContext, getState) {
  if (!isMcpContext) return null;
  const state = getState();
  return state?.canvas?.messageUUID;
}

export const GTMEngagementHandler = {
  /**
   * Initializes GTM engagement handler.
   *
   * @param  {Object} getState App Redux store.getState function
   * @param  {boolean} [isMcpContext] When true, disables iframe tracking filter so MCP apps track correctly
   */
  init({ getState, isMcpContext = false }) {
    const state = getState();
    venomEnv = state.venomEnv;

    if (usesDisableEDW(state)) return;

    if (isMcpContext) {
      const gtmId = window.EDM?.Venom?.gtmId;
      if (gtmId) {
        injectScript(`https://www.googletagmanager.com/gtm.js?id=${gtmId}`);
      }
    }

    window.dataLayer = window.dataLayer || [];
    isIframe = !isMcpContext && inNonEdmundsIframe();

    /**
     * Setups "trackAction" event listener pushing even data to GTM dataLayer
     */
    EventToolbox.on(EVENTS.TRACK_ACTION, e =>
      handleTrackAction(e, usesDisableEDWpastload(state), getCodriverMessageUUID(isMcpContext, getState))
    );

    /**
     * Setups "pageUnload" event listener starting store events in a queue
     */
    EventToolbox.on(PAGE_EVENTS.PAGE_UNLOAD, startQueueing);

    /**
     * session id / isNewSession is updated as part of a SPA transition, this event handler is to keep
     * gtm data in sync with redux state
     */
    EventToolbox.on(UPDATE_SESSION, data => {
      const { sessionId, isNewSession } = data.detail;
      const sessionContext = { session_id: sessionId, is_first_page_of_session: isNewSession };
      pushGtmDataLayerItem(sessionContext);
    });

    EventToolbox.on(EVENTS.UPDATE_GTM_VEHICLE, ({ detail: vehicle }) => {
      const vehicleContext = {
        make_id: vehicle.make && vehicle.make.id,
        model_year_id: vehicle.modelYear && vehicle.modelYear.id,
        model_link_code: vehicle.model && vehicle.model.modelLinkCode,
        publication_state: (vehicle.publicationState || PUB_STATES.NEW).toLowerCase(),
        style_id: vehicle.style && vehicle.style.id,
        ...(vehicle.vin && { vin: vehicle.vin }),
      };
      pushGtmDataLayerItem(vehicleContext);
    });

    if (document.prerendering) {
      startQueueing();
      document.addEventListener(
        'prerenderingchange',
        () => {
          processQueue();
          stopQueueing();
        },
        { once: true }
      );
    }
  },
  /**
   * Pushes initial GTM context and fires the initial page_enter event.
   *
   * @param  {Function} getState App Redux store.getState function
   * @param  {boolean} [isMcpContext] When true, page_enter follows MCP host semantics and ignores iframe embedding checks.
   * @param  {string} [mcpAgent] MCP provider identifier (e.g. 'codriver'). Passed directly instead of reading from Redux.
   */
  pushInitialContext({ getState, isMcpContext = true, mcpAgent }) {
    if (!window.dataLayer) return;

    const state = getState();
    const pageContext = state.pageContext || {};
    const visitor = state.visitor || {};
    const url = state.url || {};
    const featureFlags = featureFlagsUtil.get() || {};
    const now = Date.now();
    const requestUrl = window.location.href;
    const doNotSell = (visitor.privacy && visitor.privacy.doNotSell) || false;
    const is304 = typeof document !== 'undefined' && document.cookie.indexOf('is304=true') > -1;
    const httpStatus = is304 ? 304 : window.EDM?.Venom?.responseStatus;

    const vehicle = pageContext.vehicle || {};
    const legacy = pageContext.legacy || {};
    const page = pageContext.page || {};
    const region = visitor.location || {};
    const pageName = legacy.pageName || page.name;
    const context = {
      platform_type: 'venom',
      request_url: requestUrl,
      referral_url: window.document.referrer,
      ua: window.navigator.userAgent,
      title: window.document.title,
      screen_resolution: [window.screen.height, window.screen.width].join('x'),
      browser_width: window.innerWidth,
      geoloc_enabled: 'geolocation' in window,

      page_name: pageName,
      page_category: legacy.categoryName || page.category,
      page_template: page.name,
      page_uuid: now,
      publication_state: (vehicle.publicationState || PUB_STATES.NEW).toLowerCase(),
      make_id: vehicle.make && vehicle.make.id,
      make_nicename: vehicle.make && vehicle.make.niceName,
      make_adtargetid: vehicle.make && vehicle.make.adTargetId,
      model_year_id: vehicle.modelYear && vehicle.modelYear.id,
      model_nicename: vehicle.model && vehicle.model.niceName,
      model_name: vehicle.model && vehicle.model.name,
      model_adtargetid: vehicle.model && vehicle.model.adTargetId,
      submodel_id: vehicle.subModel && vehicle.subModel.id,
      submodel_nicename: vehicle.subModel && vehicle.subModel.niceName,
      submodel_adtargetid: vehicle.subModel && vehicle.subModel.adTargetId,
      model_link_code: vehicle.model && vehicle.model.modelLinkCode,
      year: vehicle.modelYear && vehicle.modelYear.year,
      type: vehicle.type && vehicle.type.niceName,
      type_list: vehicle.typeList,
      price: vehicle.displayPrice,
      sale_price: vehicle.salePrice,
      vin: vehicle.vin,
      trim: vehicle.trim,
      extColor: simplifyColorName(vehicle.exteriorColor),
      color: simplifyColorName(vehicle.interiorColor),
      style_id: vehicle.style && vehicle.style.id,
      partner_id: page.partnerId || pageContext.dealer?.rooftopId,

      visitor_id: visitor.id,
      capi_ext_id: visitor.capiExtId,
      session_id: visitor.session,
      is_first_page_of_session: visitor.isNewSession,
      ias_data: visitor.iasData,
      http_status: httpStatus,
      usprivacy: visitor.privacy && visitor.privacy.uspString,
      donotsell: doNotSell,
      user_zip_code: region.zipCode,
      ip_zip_code: region.ipZipCode,
      dma: region.dma || '',
      url_pattern: url.context && url.context.urlPattern,
      client_ip_address: visitor.trueClientIp,
      enableServerSideGTM: !!featureFlags.enableServerSideGTM,
      should_not_sell_via_cipa_policy: !!featureFlags.shouldNotSellViaCIPAPolicy,

      ts: now,
      tsp: now,
      timestamp: now,
    };

    if (pageName === 'new_model_core' && !region.userSet) {
      delete context.user_zip_code;
    }

    const externalTagAttribute = pageContext.externalTagAttribute || '';
    if (externalTagAttribute) {
      context.ext_tag_include = externalTagAttribute;
    }

    if (isMcpContext) {
      context.synpartner = getMcpSynpartner(mcpAgent);
    } else if (inNonEdmundsIframe()) {
      context.synpartner = 'embed';
    }

    const canvasRefMatch = new RegExp(/[?&]canvas_ref=([^&]*)/).exec(requestUrl);
    if (canvasRefMatch) {
      context.canvas_ref = decodeURIComponent(canvasRefMatch[1]);
    }

    window.dataLayer.push(
      (function getConsentArgs() {
        // eslint-disable-next-line prefer-rest-params
        return arguments;
      })('consent', 'update', {
        ad_storage: doNotSell ? 'denied' : 'granted',
        ad_user_data: doNotSell ? 'denied' : 'granted',
        ad_personalization: doNotSell ? 'denied' : 'granted',
        analytics_storage: 'granted',
      })
    );

    pushGtmDataLayerItem(context);

    const shouldFirePageEnter = isMcpContext ? mcpAgent !== MCP_AGENT_CODRIVER : !inNonEdmundsIframe();
    if (shouldFirePageEnter && pageName !== 'chat_canvas_assistant') {
      const pageEnterEvent = {
        event: 'page_enter',
        event_type: 'page_enter',
        ts: now,
        event_data: {
          action_cause: 'page_load',
          http_status: httpStatus,
        },
      };
      pushGtmDataLayerItem(pageEnterEvent);
    }
  },
};
